import { Button, Empty, Pagination } from "antd";
import {
  ContentFilters,
  ContentTopBar,
  Loader,
  RecordCountBadge,
  StyledPaginationWrapper,
} from "components";
import { ResultError } from "components/errors";
import {
  MainFilters,
  MainFiltersParams,
} from "components/main-filters/main-filters";
import {
  DayViewFiltersForm,
  DayViewFiltersFormValues,
  EmptyStateWrapper,
  FieldsTimetable,
  StyledTimetableBackground,
  TimetableContainer,
  TimetableLegend,
  TimetableMetadataWrapper,
} from "features/bookings/components";
import { useGetFieldsBookings } from "features/bookings/use-cases";
import { FieldsBookingsParams } from "features/fields";
import {
  canViewDayViewClientStats,
  usePermissions,
  useUserContext,
} from "features/users";
import { usePaginationParams } from "hooks";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDate } from "utils/date";

import DayViewStatsComponent from "./day-view-stats-component";
const initialParams: FieldsBookingsParams = {
  page: 1,
  size: 20,
  date: formatDate({
    date: new Date(),
    format: "isoDate",
  }),
};

export const DayView = () => {
  const { t } = useTranslation();
  const timetableRef = useRef<HTMLDivElement>(null);
  const [componentTopOffset, setComponentTopOffset] = useState(0);
  const permissions = usePermissions();
  const [zoomLevel, setZoomLevel] = useState(1);

  const { params, setParams, setPage } =
    usePaginationParams<FieldsBookingsParams>(initialParams);

  const { data, isError, isFetching, isLoading, isSuccess, status } =
    useGetFieldsBookings(params);
  const hasResults = isSuccess && !isFetching && data.total > 0;

  useLayoutEffect(() => {
    if (timetableRef.current) {
      setComponentTopOffset(timetableRef.current.offsetTop);
    }
  }, []);

  const handleFiltersChange = (values: DayViewFiltersFormValues) => {
    setParams((prevParams) => ({
      ...prevParams,
      ...values,
      page: 1,
      date: formatDate({
        date: values.date,
        format: "isoDate",
      }),
    }));
  };

  const handleMainFiltersChange = (mainParams: MainFiltersParams) => {
    setParams((prevParams) => ({
      ...prevParams,
      company_type: permissions?.companyType.select
        ? mainParams?.company_type
        : undefined,
      facility_id: permissions?.facility.select
        ? mainParams?.facility_id
        : undefined,
      company_id: permissions?.company.select
        ? mainParams?.company_id
        : undefined,
      page: 1,
    }));
  };

  const formId = "day-view-filters-form";
  const ndate = params?.date;
  const company_id = params?.company_id;
  const facility_id = params?.facility_id;
  const field_id = params?.field_id;
  const { user } = useUserContext();

  return (
    <>
      <ContentTopBar
        title={t("dayView.heading")}
        setZoomLevel={setZoomLevel}
        ctFilter={
          <ContentFilters formId={formId}>
            <DayViewFiltersForm
              formId={formId}
              handleFilter={handleFiltersChange}
              initialValues={initialParams}
            />
          </ContentFilters>
        }
        // renderHeadingContent={
        //   <MainFilters onChange={handleMainFiltersChange} />
        // }
        renderHeadingContent={
          <>
            <MainFilters onChange={handleMainFiltersChange} />
          </>
        }
      />
      {canViewDayViewClientStats(user) && (
        <DayViewStatsComponent
          date={ndate}
          company_id={company_id}
          facility_id={facility_id}
          field_id={field_id}
        />
      )}
      {/* <ContentFilters formId={formId}>
        <DayViewFiltersForm
          formId={formId}
          handleFilter={handleFiltersChange}
          initialValues={initialParams}
        />
      </ContentFilters> */}

      <div className="pgtl_zmbtn tbsbbx">
        <TimetableMetadataWrapper>
          <RecordCountBadge
            text={t("dayView.fieldsFound")}
            count={data?.total || 0}
          />
          <TimetableLegend />
        </TimetableMetadataWrapper>

        <div className="mzbtngrp titlenone">
          <Button
            className="btn"
            onClick={() => setZoomLevel((z) => Math.min(2, z + 0.1))}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M11 11V5H13V11H19V13H13V19H11V13H5V11H11Z"></path>
            </svg>
            <span className="ttlNm">Zoom In</span>
          </Button>
          <Button
            className="btn"
            onClick={() => setZoomLevel((z) => Math.max(0.5, z - 0.1))}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M5 11V13H19V11H5Z"></path>
            </svg>
            <span className="ttlNm">Zoom Out</span>
          </Button>
          <Button className="btn" onClick={() => setZoomLevel(1)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M22 12C22 17.5228 17.5229 22 12 22C6.4772 22 2 17.5228 2 12C2 6.47715 6.4772 2 12 2V4C7.5817 4 4 7.58172 4 12C4 16.4183 7.5817 20 12 20C16.4183 20 20 16.4183 20 12C20 9.53614 18.8862 7.33243 17.1346 5.86492L15 8V2L21 2L18.5535 4.44656C20.6649 6.28002 22 8.9841 22 12Z"></path>
            </svg>
            <span className="ttlNm">Reset Zoom</span>
          </Button>
        </div>
      </div>

      <StyledTimetableBackground className="slot_wrap day_view_clg">
        <TimetableContainer
          ref={timetableRef}
          $offset={componentTopOffset}
          status={!hasResults ? "empty" : status}
          headerHeight={215}
        >
          {isLoading || isFetching ? (
            <Loader />
          ) : isError ? (
            <ResultError />
          ) : !hasResults ? (
            <EmptyStateWrapper>
              <Empty description={<>{t("dayView.empty")}</>} />
            </EmptyStateWrapper>
          ) : (
            <FieldsTimetable
              data={data}
              date={formatDate({ date: params.date, format: "isoDate" })}
              filteredSportId={params.sport_id}
              zoomLevel={zoomLevel}
            />
          )}
        </TimetableContainer>
      </StyledTimetableBackground>

      <StyledPaginationWrapper>
        <Pagination
          current={data?.page}
          total={data?.total}
          pageSize={params.size} // bind current size from params
          onChange={setPage}
          onShowSizeChange={(page, size) => {
            setParams((prev) => ({
              ...prev,
              page: 1, // reset to page 1 when size changes
              size,
            }));
          }}
          showSizeChanger
          pageSizeOptions={["20", "50", "100"]}
          size="small"
        />
      </StyledPaginationWrapper>
    </>
  );
};
